/**
 * PlatformBillingPanel — Admin-Tab „SB-Abrechnung" (Juni 2026).
 * Faktura ohne Stripe: Absenderdaten, SB-Abrechnungs-Konfiguration,
 * Rechnungen (generieren/bearbeiten/senden/bezahlt), Mahnwesen, Upgrade-Anfragen.
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Loader2, FileText, Send, CheckCircle2, Ban, Bell, Download,
  Settings2, ChevronDown, ChevronRight, Plus, Trash2, Crown,
} from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n ?? 0);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE') : '—');

const PERIOD_OPTIONS = [
  { value: 'monthly', label: 'Monatlich' },
  { value: 'quarterly', label: 'Quartal' },
  { value: 'half_yearly', label: 'Halbjahr' },
  { value: 'yearly', label: 'Jahr' },
];
const STATUS_BADGE = {
  draft: 'bg-gray-100 text-gray-700 border-gray-200',
  sent: 'bg-blue-100 text-blue-800 border-blue-200',
  paid: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  cancelled: 'bg-gray-100 text-gray-400 border-gray-200 line-through',
};
const STATUS_LABEL = { draft: 'Entwurf', sent: 'Offen', paid: 'Bezahlt', cancelled: 'Storniert' };

const SETTINGS_FIELDS = [
  ['company_name', 'Firmierung'], ['address_line1', 'Straße'], ['address_line2', 'PLZ / Ort'],
  ['country', 'Land'], ['vat_id', 'UID-Nummer'], ['iban', 'IBAN'], ['bic', 'BIC'],
  ['bank_name', 'Bank'], ['email_from', 'Absender-E-Mail'], ['email_from_name', 'Absender-Name'],
];

export default function PlatformBillingPanel() {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [advisors, setAdvisors] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [upgradeRequests, setUpgradeRequests] = useState([]);
  const [invoiceFilter, setInvoiceFilter] = useState('all');
  const [dialog, setDialog] = useState(null); // {advisor, period_start, period_end, line_items, notes, totals}
  const [busy, setBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [s, a, i, u] = await Promise.all([
        api.get('/admin/billing/settings'),
        api.get('/admin/billing/advisors'),
        api.get('/admin/billing/invoices'),
        api.get('/admin/billing/upgrade-requests'),
      ]);
      setSettings(s.data);
      setAdvisors(a.data.advisors);
      setInvoices(i.data.invoices);
      setUpgradeRequests(u.data.requests);
    } catch {
      toast.error('Abrechnungsdaten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveSettings = async () => {
    try {
      await api.put('/admin/billing/settings', settings);
      toast.success('Absenderdaten gespeichert.');
      setShowSettings(false);
    } catch { toast.error('Speichern fehlgeschlagen.'); }
  };

  const updateConfig = async (advisorId, patch) => {
    try {
      await api.put(`/admin/billing/advisors/${advisorId}/config`, patch);
      toast.success('Konfiguration aktualisiert.');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler beim Aktualisieren.'); }
  };

  const openInvoiceDialog = async (adv) => {
    setDialog({ advisor: adv, period_start: adv.proposed_period_start, period_end: adv.proposed_period_end, line_items: null, notes: '', totals: null });
  };

  const previewInvoice = async () => {
    setBusy(true);
    try {
      const res = await api.post('/admin/billing/invoices/preview', {
        advisor_id: dialog.advisor.id, period_start: dialog.period_start, period_end: dialog.period_end,
      });
      setDialog((d) => ({ ...d, line_items: res.data.line_items, totals: res.data }));
      if (res.data.line_items.length === 0) toast.warning('Keine abrechenbaren Positionen in dieser Periode.');
    } catch (err) { toast.error(err.response?.data?.detail || 'Vorschau fehlgeschlagen.'); }
    finally { setBusy(false); }
  };

  const updateLine = (idx, key, value) => {
    setDialog((d) => {
      const items = d.line_items.map((li, i) => {
        if (i !== idx) return li;
        const next = { ...li, [key]: key === 'description' ? value : parseFloat(value) || 0 };
        next.total = Math.round(next.quantity * next.unit_price * 100) / 100;
        return next;
      });
      return { ...d, line_items: items };
    });
  };

  const createInvoice = async (sendDirectly) => {
    setBusy(true);
    try {
      const res = await api.post('/admin/billing/invoices', {
        advisor_id: dialog.advisor.id,
        period_start: dialog.period_start,
        period_end: dialog.period_end,
        line_items: dialog.line_items,
        notes: dialog.notes || null,
      });
      if (sendDirectly) {
        await api.post(`/admin/billing/invoices/${res.data.id}/send`);
        toast.success(`Rechnung ${res.data.invoice_number} erstellt & versendet.`);
      } else {
        toast.success(`Entwurf ${res.data.invoice_number} erstellt.`);
      }
      setDialog(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Rechnung konnte nicht erstellt werden.'); }
    finally { setBusy(false); }
  };

  const invoiceAction = async (inv, action, label) => {
    try {
      const res = await api.post(`/admin/billing/invoices/${inv.id}/${action}`);
      toast.success(`${label}${res.data.email_sent === false ? ' (E-Mail im Preview gemockt)' : ''}`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Aktion fehlgeschlagen.'); }
  };

  const downloadPdf = async (inv) => {
    try {
      const res = await api.get(`/admin/billing/invoices/${inv.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('PDF konnte nicht geladen werden.'); }
  };

  const resolveRequest = async (id) => {
    try { await api.post(`/admin/billing/upgrade-requests/${id}/resolve`); fetchAll(); }
    catch { toast.error('Fehler.'); }
  };

  const downloadConsentLog = async () => {
    try {
      const res = await api.get('/admin/billing/terms-acceptances/export', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'sb-zustimmungsprotokoll.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export fehlgeschlagen.'); }
  };

  if (loading) return <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-emerald-600" /></div>;

  const filteredInvoices = invoiceFilter === 'all' ? invoices : invoices.filter((i) => (invoiceFilter === 'overdue' ? i.status === 'sent' && i.days_overdue > 0 : i.status === invoiceFilter));
  const dialogSubtotal = dialog?.line_items ? dialog.line_items.reduce((s, li) => s + li.total, 0) : 0;

  return (
    <div className="space-y-6" data-testid="platform-billing-panel">
      {/* Zustimmungsprotokoll-Export (rechtssicherer Nachweis, Juni 2026) */}
      <div className="flex justify-end">
        <Button size="sm" variant="outline" onClick={downloadConsentLog} data-testid="export-consent-log-btn">
          <Download className="mr-2 h-3.5 w-3.5" /> Zustimmungsprotokoll (CSV)
        </Button>
      </div>

      {/* Upgrade-Anfragen */}
      {upgradeRequests.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50" data-testid="upgrade-requests-card">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Crown className="h-4 w-4 text-amber-600" /> Offene Upgrade-Anfragen ({upgradeRequests.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {upgradeRequests.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 bg-white rounded-lg border border-amber-100 px-3 py-2 text-sm" data-testid={`upgrade-request-${r.id}`}>
                <div>
                  <span className="font-medium">{r.advisor_name || r.advisor_email}</span>
                  <span className="text-gray-500"> möchte auf </span>
                  <span className="font-semibold uppercase">{r.tier}</span>
                  {r.note && <p className="text-xs text-gray-500 mt-0.5">„{r.note}"</p>}
                </div>
                <Button size="sm" variant="outline" onClick={() => resolveRequest(r.id)} data-testid={`resolve-request-${r.id}`}>Erledigt</Button>
              </div>
            ))}
            <p className="text-xs text-amber-700">Tarif unten in der SB-Konfiguration umstellen (Modus „Rechnung"), dann Anfrage als erledigt markieren.</p>
          </CardContent>
        </Card>
      )}

      {/* Absenderdaten */}
      <Card>
        <button type="button" className="w-full text-left" onClick={() => setShowSettings(!showSettings)} data-testid="billing-settings-toggle">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              {showSettings ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <Settings2 className="h-4 w-4 text-gray-500" /> Absenderdaten (Rechnungs-PDF)
            </CardTitle>
            <CardDescription>{settings?.company_name} · {settings?.vat_id} · {settings?.email_from}</CardDescription>
          </CardHeader>
        </button>
        {showSettings && (
          <CardContent className="grid gap-3 sm:grid-cols-2" data-testid="billing-settings-form">
            {SETTINGS_FIELDS.map(([key, label]) => (
              <div key={key}>
                <label className="text-xs font-medium text-gray-600">{label}</label>
                <Input value={settings?.[key] || ''} onChange={(e) => setSettings((s) => ({ ...s, [key]: e.target.value }))} className="mt-1" data-testid={`billing-setting-${key}`} />
              </div>
            ))}
            <div className="sm:col-span-2">
              <Button onClick={saveSettings} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="billing-settings-save">Speichern</Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* SB-Konfiguration */}
      <Card data-testid="billing-advisors-card">
        <CardHeader className="pb-2"><CardTitle className="text-base">SB-Abrechnungs-Konfiguration</CardTitle>
          <CardDescription>Tarif, Modus (Rechnung/Komp) und Abrechnungsperiode pro Kanzlei. Mandantenlizenz: 16 €/Monat, Stichtag Monatsanfang.</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-[11px] uppercase tracking-wider text-gray-400">
              <th className="px-4 py-2 font-medium">Kanzlei</th><th className="px-2 py-2 font-medium">Land</th>
              <th className="px-2 py-2 font-medium">Tarif</th><th className="px-2 py-2 font-medium">Modus</th>
              <th className="px-2 py-2 font-medium">Periode</th><th className="px-2 py-2 font-medium text-center">Lizenzen</th>
              <th className="px-2 py-2 font-medium text-right">Offen</th><th className="px-4 py-2"></th>
            </tr></thead>
            <tbody>
              {advisors.map((adv) => (
                <tr key={adv.id} className="border-b last:border-0" data-testid={`billing-advisor-row-${adv.id}`}>
                  <td className="px-4 py-2.5">
                    <p className="font-medium text-gray-900">{adv.company_name || adv.name || '—'}</p>
                    <p className="text-xs text-gray-400">{adv.email}</p>
                  </td>
                  <td className="px-2 py-2.5">
                    <select value={adv.billing_country} onChange={(e) => updateConfig(adv.id, { billing_country: e.target.value })} className="text-xs border border-gray-200 rounded px-1 py-1 bg-white" data-testid={`cfg-country-${adv.id}`}>
                      {['AT', 'DE', 'CH'].map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-2.5">
                    <select value={adv.billing_tier} onChange={(e) => updateConfig(adv.id, { billing_tier: e.target.value })} className="text-xs border border-gray-200 rounded px-1 py-1 bg-white" data-testid={`cfg-tier-${adv.id}`}>
                      <option value="starter">Starter</option><option value="growth">Growth</option><option value="pro">Pro</option>
                    </select>
                  </td>
                  <td className="px-2 py-2.5">
                    <select value={adv.billing_status} onChange={(e) => updateConfig(adv.id, { billing_status: e.target.value })} className="text-xs border border-gray-200 rounded px-1 py-1 bg-white" data-testid={`cfg-status-${adv.id}`}>
                      <option value="none">—</option><option value="invoice">Rechnung</option><option value="comp">Komp (gratis)</option>
                    </select>
                  </td>
                  <td className="px-2 py-2.5">
                    <select value={adv.billing_period} onChange={(e) => updateConfig(adv.id, { billing_period: e.target.value })} className="text-xs border border-gray-200 rounded px-1 py-1 bg-white" data-testid={`cfg-period-${adv.id}`}>
                      {PERIOD_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-2.5 text-center text-gray-700">{adv.licensed_clients}<span className="text-gray-400"> / {adv.active_clients}</span></td>
                  <td className="px-2 py-2.5 text-right">
                    {adv.open_amount > 0 ? (
                      <span className={adv.overdue_invoices > 0 ? 'text-rose-600 font-semibold' : 'text-gray-700'}>{fmt(adv.open_amount)}</span>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Button size="sm" variant="outline" onClick={() => openInvoiceDialog(adv)} className="text-xs" data-testid={`create-invoice-btn-${adv.id}`}>
                      <FileText className="mr-1 h-3 w-3" /> Rechnung
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Rechnungen */}
      <Card data-testid="billing-invoices-card">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <div><CardTitle className="text-base">Rechnungen</CardTitle><CardDescription>Fällig sofort nach Erhalt · Mahnwesen: Erinnerung ab 7 T., 1. Mahnung ab 14 T. (+5 €), 2. Mahnung ab 21 T. (+10 €)</CardDescription></div>
          <select value={invoiceFilter} onChange={(e) => setInvoiceFilter(e.target.value)} className="text-xs border border-gray-200 rounded px-2 py-1.5 bg-white" data-testid="invoice-filter">
            <option value="all">Alle</option><option value="draft">Entwürfe</option><option value="sent">Offen</option>
            <option value="overdue">Überfällig</option><option value="paid">Bezahlt</option><option value="cancelled">Storniert</option>
          </select>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          {filteredInvoices.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-gray-400">Keine Rechnungen{invoiceFilter !== 'all' ? ' in diesem Filter' : ''}.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-[11px] uppercase tracking-wider text-gray-400">
                <th className="px-4 py-2 font-medium">Nr.</th><th className="px-2 py-2 font-medium">Kanzlei</th>
                <th className="px-2 py-2 font-medium">Periode</th><th className="px-2 py-2 font-medium text-right">Betrag</th>
                <th className="px-2 py-2 font-medium">Status</th><th className="px-4 py-2 font-medium text-right">Aktionen</th>
              </tr></thead>
              <tbody>
                {filteredInvoices.map((inv) => (
                  <tr key={inv.id} className="border-b last:border-0" data-testid={`invoice-row-${inv.invoice_number}`}>
                    <td className="px-4 py-2.5 font-mono text-xs">{inv.invoice_number}</td>
                    <td className="px-2 py-2.5">{inv.recipient?.company_name || inv.recipient?.name}</td>
                    <td className="px-2 py-2.5 text-xs text-gray-500">{inv.period_label}</td>
                    <td className="px-2 py-2.5 text-right font-mono">{fmt(inv.total)}{inv.dunning_fees_total > 0 && <span className="text-xs text-rose-500"> +{fmt(inv.dunning_fees_total)}</span>}</td>
                    <td className="px-2 py-2.5">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATUS_BADGE[inv.status]}`}>
                        {STATUS_LABEL[inv.status]}{inv.status === 'sent' && inv.days_overdue > 0 && ` · ${inv.days_overdue} T. überfällig`}
                      </span>
                      {inv.dunning_level > 0 && <span className="ml-1 text-[10px] text-rose-500">Mahnstufe {inv.dunning_level}</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => downloadPdf(inv)} title="PDF" className="p-1.5 rounded hover:bg-gray-100 text-gray-500" data-testid={`pdf-btn-${inv.invoice_number}`}><Download className="h-3.5 w-3.5" /></button>
                        {inv.status === 'draft' && (
                          <button onClick={() => invoiceAction(inv, 'send', 'Rechnung versendet.')} title="Senden" className="p-1.5 rounded hover:bg-blue-50 text-blue-600" data-testid={`send-btn-${inv.invoice_number}`}><Send className="h-3.5 w-3.5" /></button>
                        )}
                        {inv.status === 'sent' && (
                          <button onClick={() => invoiceAction(inv, 'mark-paid', 'Als bezahlt markiert.')} title="Bezahlt" className="p-1.5 rounded hover:bg-emerald-50 text-emerald-600" data-testid={`paid-btn-${inv.invoice_number}`}><CheckCircle2 className="h-3.5 w-3.5" /></button>
                        )}
                        {inv.available_dunning && (
                          <button onClick={() => invoiceAction(inv, 'dunning', `${inv.available_dunning.label} versendet.`)} title={inv.available_dunning.label} className="p-1.5 rounded hover:bg-rose-50 text-rose-600" data-testid={`dunning-btn-${inv.invoice_number}`}><Bell className="h-3.5 w-3.5" /></button>
                        )}
                        {inv.status !== 'paid' && inv.status !== 'cancelled' && (
                          <button onClick={() => invoiceAction(inv, 'cancel', 'Rechnung storniert.')} title="Stornieren" className="p-1.5 rounded hover:bg-gray-100 text-gray-400" data-testid={`cancel-btn-${inv.invoice_number}`}><Ban className="h-3.5 w-3.5" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Rechnung-erstellen-Dialog */}
      {dialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4" data-testid="invoice-dialog-overlay">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl max-h-[90vh] overflow-y-auto p-6" data-testid="invoice-dialog">
            <h3 className="text-lg font-semibold text-gray-900">Rechnung erstellen — {dialog.advisor.company_name || dialog.advisor.name}</h3>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600">Periode von</label>
                <Input type="date" value={dialog.period_start} onChange={(e) => setDialog((d) => ({ ...d, period_start: e.target.value, line_items: null }))} className="mt-1" data-testid="invoice-period-start" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Periode bis</label>
                <Input type="date" value={dialog.period_end} onChange={(e) => setDialog((d) => ({ ...d, period_end: e.target.value, line_items: null }))} className="mt-1" data-testid="invoice-period-end" />
              </div>
            </div>

            {!dialog.line_items ? (
              <Button onClick={previewInvoice} disabled={busy} className="mt-4 bg-emerald-600 text-white hover:bg-emerald-700" data-testid="invoice-preview-btn">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Positionen berechnen
              </Button>
            ) : (
              <>
                <div className="mt-4 space-y-2" data-testid="invoice-line-items">
                  {dialog.line_items.map((li, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input value={li.description} onChange={(e) => updateLine(idx, 'description', e.target.value)} className="flex-1 text-xs" />
                      <Input type="number" value={li.quantity} onChange={(e) => updateLine(idx, 'quantity', e.target.value)} className="w-16 text-xs text-right" />
                      <Input type="number" step="0.01" value={li.unit_price} onChange={(e) => updateLine(idx, 'unit_price', e.target.value)} className="w-20 text-xs text-right" />
                      <span className="w-20 text-right font-mono text-xs">{fmt(li.total)}</span>
                      <button onClick={() => setDialog((d) => ({ ...d, line_items: d.line_items.filter((_, i) => i !== idx) }))} className="p-1 text-gray-300 hover:text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                  <button
                    onClick={() => setDialog((d) => ({ ...d, line_items: [...d.line_items, { description: '', quantity: 1, unit_price: 0, total: 0 }] }))}
                    className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:underline" data-testid="invoice-add-line"
                  ><Plus className="h-3 w-3" /> Position hinzufügen</button>
                </div>
                <div className="mt-3 text-right text-sm">
                  <p className="text-gray-500">Netto: <span className="font-mono">{fmt(dialogSubtotal)}</span></p>
                  <p className="text-xs text-gray-400">USt nach Kanzlei-Land ({dialog.advisor.billing_country}): {dialog.advisor.billing_country === 'AT' ? '20 %' : '0 % (Reverse Charge / nicht steuerbar)'}</p>
                </div>
                <div className="mt-3">
                  <label className="text-xs font-medium text-gray-600">Hinweistext (optional, erscheint auf der Rechnung)</label>
                  <Input value={dialog.notes} onChange={(e) => setDialog((d) => ({ ...d, notes: e.target.value }))} className="mt-1" data-testid="invoice-notes" />
                </div>
              </>
            )}

            <div className="mt-6 flex items-center justify-between">
              <Button variant="ghost" onClick={() => setDialog(null)} data-testid="invoice-dialog-cancel">Abbrechen</Button>
              {dialog.line_items && dialog.line_items.length > 0 && (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => createInvoice(false)} disabled={busy} data-testid="invoice-create-draft-btn">Als Entwurf speichern</Button>
                  <Button onClick={() => createInvoice(true)} disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="invoice-create-send-btn">
                    {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Erstellen & senden
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
